import { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { Modal } from '../../components/Modal';

// Parametros operacionais do ponto (so admin). Gravam em app_settings e valem na
// hora, sem restart. Tolerancia e pausa irreal seguem a regra do dia; o teto semanal
// alimenta a regua de minutos do bonus (perde se a soma da semana >= teto; 0 = desligado).
const CHAVE_TOLERANCIA = 'ponto_tolerancia_atraso_min';
const CHAVE_PAUSA = 'ponto_pausa_irreal_min';
const CHAVE_TETO = 'ponto_total_atraso_semana_min';

function NumeroMin({ label, ajuda, valor, onChange }: { label: string; ajuda: string; valor: string; onChange: (v: string) => void }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="text"
          inputMode="numeric"
          value={valor}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
          style={{ width: 90, textAlign: 'right' }}
        />
        <span style={{ color: 'var(--text-muted)' }}>min</span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{ajuda}</div>
    </div>
  );
}

export function ConfigPontoModal({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const toast = useToast();
  const [tolerancia, setTolerancia] = useState('3');
  const [pausa, setPausa] = useState('5');
  const [teto, setTeto] = useState('0');
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setCarregando(true);
    const ler = (chave: string) => api.get<{ valor: string | null }>(`/api/app-settings/${chave}`).catch(() => ({ valor: null }));
    Promise.all([ler(CHAVE_TOLERANCIA), ler(CHAVE_PAUSA), ler(CHAVE_TETO)])
      .then(([t, p, te]) => {
        setTolerancia(t.valor ?? '3');
        setPausa(p.valor ?? '5');
        setTeto(te.valor ?? '0');
      })
      .finally(() => setCarregando(false));
  }, [aberto]);

  async function salvar() {
    setSalvando(true);
    try {
      await Promise.all([
        api.put(`/api/app-settings/${CHAVE_TOLERANCIA}`, { valor: String(parseInt(tolerancia || '0', 10)) }),
        api.put(`/api/app-settings/${CHAVE_PAUSA}`, { valor: String(parseInt(pausa || '0', 10)) }),
        api.put(`/api/app-settings/${CHAVE_TETO}`, { valor: String(parseInt(teto || '0', 10)) }),
      ]);
      toast.sucesso('Configuracoes do ponto salvas');
      onFechar();
    } catch (e: any) {
      toast.erro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo="Configuracoes do ponto"
      largura={460}
      rodape={
        <button type="button" className="btn-primario" onClick={() => void salvar()} disabled={salvando || carregando}>
          <Save size={14} /> {salvando ? 'Salvando...' : 'Salvar'}
        </button>
      }
    >
      {carregando ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : (
        <div>
          <NumeroMin
            label="Tolerancia de atraso"
            ajuda="Margem do dia: so marca atraso se a entrada passar do horario + esta tolerancia."
            valor={tolerancia}
            onChange={setTolerancia}
          />
          <NumeroMin
            label="Pausa irreal"
            ajuda="Saida + entrada com menos que isto viram anomalia (provavel batida errada/dupla)."
            valor={pausa}
            onChange={setPausa}
          />
          <NumeroMin
            label="Total de atraso na semana (teto do bonus)"
            ajuda="Soma TODOS os minutos de atraso da semana (ate os dentro da tolerancia). Se a soma chegar a este teto, perde o bonus da semana. 0 = desligado."
            valor={teto}
            onChange={setTeto}
          />
        </div>
      )}
    </Modal>
  );
}
